"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet"
import { Menu, X, Info, History, Download, Sun, Moon, Grid, Grid3X3 } from "lucide-react"
import { useTheme } from "next-themes"
import { useLanguage } from "@/lib/i18n/language-provider"
import { LanguageSwitcher } from "@/components/language-switcher"
import SmartLink from "@/components/smart-link"

interface BurgerMenuProps {
  className?: string
  showDownloads?: boolean
}

export function BurgerMenu({ className, showDownloads = true }: BurgerMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const { theme, setTheme } = useTheme()
  const { t } = useLanguage()

  const closeMenu = () => setIsOpen(false)

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="icon" className={className}>
          <Menu className="h-4 w-4" />
          <span className="sr-only">Ouvrir le menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-80">
        <SheetHeader>
          <SheetTitle className="flex items-center justify-between">
            Menu
          </SheetTitle>
        </SheetHeader>
        
        <div className="flex flex-col gap-4 mt-6">
          {/* Boutons de navigation */}
          <Button variant="outline" className="flex items-center gap-2 justify-start w-full" asChild>
            <SmartLink href="/multigrid/" onClick={closeMenu}>
              <Grid className="h-4 w-4 text-blue-500" />
              {t("Multi-Grid") || "Multi-Grid"}
            </SmartLink>
          </Button>

          <Button variant="outline" className="flex items-center gap-2 justify-start w-full" asChild>
            <SmartLink href="/synoptique/" onClick={closeMenu}>
              <Grid3X3 className="h-4 w-4 text-green-500" />
              {t("landing.header.synoptic")}
            </SmartLink>
          </Button>

          <Button variant="outline" className="flex items-center gap-2 justify-start w-full" asChild>
            <SmartLink href="/about/" onClick={closeMenu}>
              <Info className="h-4 w-4" />
              {t("nav.about")}
            </SmartLink>
          </Button>

          <Button variant="outline" className="flex items-center gap-2 justify-start w-full" asChild>
            <SmartLink href="/changelogs/" onClick={closeMenu}>
              <History className="h-4 w-4" />
              {t("nav.changelogs")}
            </SmartLink>
          </Button>

          {showDownloads && (
            <Button variant="outline" className="flex items-center gap-2 justify-start w-full" asChild>
              <SmartLink href="/downloads/" onClick={closeMenu}>
                <Download className="h-4 w-4" />
                {t("nav.downloads")}
              </SmartLink>
            </Button>
          )}

          {/* Séparateur */}
          <div className="border-t my-2"></div>
          
          {/* Paramètres */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Langue</span>
            <LanguageSwitcher />
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Thème</span>
            <Button variant="outline" size="icon" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
